import { useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { useMutation } from '@redwoodjs/web';

import Button from 'src/components/Button';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { FieldInput } from 'src/components/FormikComponents';
import { InputWithSearch } from 'src/components/InputWithSearch';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { AddressListModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ADD_MERCHANT } from 'src/graphql/mutations/addMerchant.mutation';
import { useAddressLookUp } from 'src/hooks/useAddressLookup';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { isIreland } from 'src/lib/countryConfig';
import { MerchantInformation_FormValues as MerchantInformationFormValues } from 'src/types/AddMerchant/merchantInfo';
import { supportedCountries } from 'src/utils/utils';
import { Address } from 'types/graphql';

interface EditRegisteredAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    applicationId: number;
    merchantInformation?: MerchantInformationFormValues;
    onUpdate?: () => void;
}

interface AddressFormValues {
    addressLine1: string;
    addressLine2: string;
    townOrCity: string;
    county: string;
    country: string;
    eircode: string;
}

const EditRegisteredAddressModal = ({
    isOpen,
    onClose,
    applicationId,
    merchantInformation,
    onUpdate,
}: EditRegisteredAddressModalProps) => {
    const [formattedAddress, setFormattedAddress] = useState('');
    const [showAddressList, setShowAddressList] = useState(false);
    const { addresses, findAddress } = useAddressLookUp();
    const registeredAddress = merchantInformation?.aboutBusiness?.registeredAddress;
    const { getPostalLabel } = useCountryHelpers();
    const validationSchema = Yup.object({
        addressLine1: Yup.string().required('Address Line 1 is required'),
        townOrCity: Yup.string().required('Town/City is required'),
        country: Yup.string().required('Country is required'),
        addressLine2: Yup.string(),
        county: Yup.string(),
        eircode: Yup.string(),
    });

    const [addMerchant, { loading: updating }] = useMutation(ADD_MERCHANT, {
        onCompleted: () => {
            showSuccessToast('Address updated successfully');
            onUpdate?.();
            onClose();
        },
        onError: (error: unknown) => {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to update address';
            showErrorToast(errorMessage);
        },
    });

    const handleSave = async (values: AddressFormValues) => {
        try {
            const updatedMerchantInformation: MerchantInformationFormValues = {
                ...merchantInformation,
                aboutBusiness: {
                    ...merchantInformation?.aboutBusiness,
                    registeredAddress: {
                        addressLine1: values.addressLine1.trim(),
                        addressLine2: values.addressLine2?.trim() || '',
                        townOrCity: values.townOrCity.trim(),
                        county: values.county?.trim() || '',
                        country: values.country.trim(),
                        eircode: values.eircode?.trim() || '',
                    },
                },
            };

            const input = {
                applicationId,
                merchantInformation: updatedMerchantInformation,
            };

            await addMerchant({
                variables: { input },
            });
        } catch {
            showErrorToast('Failed to update address. Please try again.');
        }
    };

    const onFindAddress = (value: string) => {
        findAddress(value, () => {
            setShowAddressList(true);
        });
    };

    const handleCloseAddressList = () => {
        setShowAddressList(false);
    };

    const handleModalClose = () => {
        setShowAddressList(false);
        setFormattedAddress('');
        onClose();
    };

    const createAddressSelectionHandler = useCallback(
        (setFieldValue: (field: string, value: string) => void) => {
            return (address: Address, text: string) => {
                setFieldValue('addressLine1', address.line1 || '');
                setFieldValue('addressLine2', address.line2 || '');
                setFieldValue('townOrCity', address.city || '');
                setFieldValue('county', address.adminAreaName || '');
                setFieldValue('country', address.countryName || '');
                setFieldValue('eircode', address.postalCode || '');
                setFormattedAddress(text);
                setShowAddressList(false);
            };
        },
        [setFormattedAddress, setShowAddressList]
    );

    const initialValues: AddressFormValues = useMemo(() => {
        return {
            addressLine1: registeredAddress?.addressLine1 || '',
            addressLine2: registeredAddress?.addressLine2 || '',
            townOrCity: registeredAddress?.townOrCity || '',
            county: registeredAddress?.county || '',
            country: registeredAddress?.country || '',
            eircode: registeredAddress?.eircode || '',
        };
    }, [registeredAddress]);

    return (
        <ModalBox
            open={isOpen}
            handleCloseModal={handleModalClose}
            title="Edit Registered Address"
            widthSize="60%"
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSave}
            >
                {({ setFieldValue }: FormikProps<AddressFormValues>) => {
                    const handleAddressSelection = createAddressSelectionHandler(setFieldValue);

                    return (
                        <>
                            <Form>
                                <Box sx={{ p: 3 }}>
                                    <Typography
                                        color="#F18F34"
                                        fontSize="20px"
                                        lineHeight="30px"
                                        fontWeight="500"
                                        mb={3}
                                    >
                                        Registered Address
                                    </Typography>

                                    {/* Search Section */}
                                    <Box mb={3}>
                                        <InputWithSearch
                                            searchText="Search"
                                            onSearch={onFindAddress}
                                            placeholder="Search for address"
                                            label="Search"
                                            fieldName="addressSearch"
                                            maskedValue={formattedAddress}
                                            clearMaskedValue={() => {
                                                return setFormattedAddress('');
                                            }}
                                            searchOnEnter
                                        />
                                    </Box>

                                    {/* Address Fields */}
                                    <Box display="flex" gap="16px" mb={2}>
                                        <Box flex={1}>
                                            <FieldInput
                                                name="addressLine1"
                                                label="Address Line 1"
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <FieldInput
                                                name="addressLine2"
                                                label="Address Line 2"
                                            />
                                        </Box>
                                    </Box>

                                    <Box display="flex" gap="16px" mb={2}>
                                        <Box flex={1}>
                                            <FieldInput name="townOrCity" label="Town/City" />
                                        </Box>
                                        <Box flex={1}>
                                            <FieldInput name="county" label="County" />
                                        </Box>
                                    </Box>

                                    <Box display="flex" gap="16px" mb={3}>
                                        <Box flex={1}>
                                            <CountrySelectInput
                                                setFieldValue={setFieldValue}
                                                filterByAlpha2Codes={supportedCountries(isIreland)}
                                                fieldName="country"
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <FieldInput name="eircode" label={getPostalLabel()} />
                                        </Box>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box display="flex" gap="16px" justifyContent="flex-end">
                                        <Button
                                            variant="back-button"
                                            type="button"
                                            onClick={handleModalClose}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="main" type="submit" disabled={updating}>
                                            {updating ? 'Updating...' : 'Confirm'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Form>

                            <AddressListModal
                                isOpen={showAddressList && addresses.length > 0}
                                addressList={addresses}
                                onClose={handleCloseAddressList}
                                onSelectedAddress={handleAddressSelection}
                            />
                        </>
                    );
                }}
            </Formik>
        </ModalBox>
    );
};

export default EditRegisteredAddressModal;
